/**
 * Las últimas listas leídas, para volver a una desde cualquier compu.
 *
 * El id del último import vivía solo en el navegador que lo subió
 * (`recordarImport`). Una lista leída y sin aplicar no aparecía en ninguna otra
 * pantalla, y una aplicada perdía su botón de Revertir en otra compu o con el
 * storage borrado. Acá están las últimas diez, sin aplicar y aplicadas.
 */
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { TituloSeccion } from "@/components/ui/titulo-seccion";
import type { Proveedor } from "@/lib/tipos";
import { formatearFechaHora } from "@/lib/utils";

import type { ImportLista } from "./logica";

export function ListasLeidas({
  imports,
  proveedores,
  onAbrir,
}: {
  imports: ReadonlyArray<ImportLista>;
  proveedores: ReadonlyArray<Pick<Proveedor, "id" | "nombre">>;
  onAbrir: (id: string) => void;
}): JSX.Element | null {
  if (imports.length === 0) return null;
  const nombreDe = (id: number | null): string =>
    proveedores.find((p) => p.id === id)?.nombre ?? "Sin proveedor";

  return (
    <div className="space-y-2">
      <TituloSeccion>Listas leídas</TituloSeccion>
      <Card padding="none" className="divide-y divide-border overflow-hidden">
        {imports.map((imp) => (
          <div key={imp.id} className="flex flex-wrap items-center justify-between gap-3 px-4 py-3">
            <span className="min-w-0">
              <span className="block truncate text-sm font-medium">
                {nombreDe(imp.proveedor_id)} · {imp.archivo_nombre}
              </span>
              <span className="block truncate text-xs text-muted-foreground">
                {formatearFechaHora(imp.created_at)} · {imp.filas_total}{" "}
                {imp.filas_total === 1 ? "fila" : "filas"}
              </span>
            </span>
            <span className="flex shrink-0 items-center gap-2">
              <Badge variant={imp.estado === "APLICADO" ? "success" : "warning"}>
                {imp.estado === "APLICADO" ? "Aplicada" : "Sin aplicar"}
              </Badge>
              <Button type="button" variant="outline" size="sm" onClick={() => onAbrir(imp.id)}>
                Abrir
              </Button>
            </span>
          </div>
        ))}
      </Card>
    </div>
  );
}
